import React, { FC, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useGetJMAFile from './funcs/useGetJMAFile';
import useAlertModal from './hooks/useAlertModal';
import {
  alertByPrefecture,
  alertLevelType,
  alertSummaryType,
} from './types';
import { areaList } from './alertConst';
import { sendAnalyticsEvent } from '../../utils/googleAnalytics';
import AutorenewIcon from '@mui/icons-material/Autorenew';

export const legendItems = [
  { label: 'pages.jmaAlerts.legend.level5', color: '#2d2a36', textColor: '#ffffff' },
  { label: 'pages.jmaAlerts.legend.level4', color: '#ad1bb9', textColor: '#ffffff' },
  { label: 'pages.jmaAlerts.legend.level3', color: '#f40e1a', textColor: '#ffffff' },
  { label: 'pages.jmaAlerts.legend.level2', color: '#ffca1a', textColor: '#1a202c' },
];

type Props = {
  // isAlertShown: boolean;
};

// ちなみに区分けの階層は以下の通り.
// https://www.jma.go.jp/jma/kishou/know/saibun/

const AlertsFromJMA: FC<Props> = () => {
  const { isLoading, isRefreshing, jmaFile, refreshJMAFile, refreshKey } = useGetJMAFile();
  const [selectedAlert, setSelectedAlert] = React.useState<alertByPrefecture | null>(null);
  const latestUpdated = jmaFile?.last_updated || '';
  const { setAlertModalShow, AlertModal } = useAlertModal(
    selectedAlert,
    latestUpdated,
    refreshJMAFile,
    isRefreshing,
    refreshKey,
  );
  const { t } = useTranslation();
  
  const alertByPrefectures = jmaFile?.alerts_by_prefectures || {};
  const handleRefresh = async () => {
    // GA4: 気象警報一覧の更新操作を計測
    sendAnalyticsEvent('jma_alert_refresh', {
      action: 'refresh',
      source: 'summary',
    });
    await refreshJMAFile();
  };
  const handleDetailClick = (alert: alertByPrefecture) => () => {
    // GA4: 気象警報詳細の表示を計測
    sendAnalyticsEvent('jma_alert_detail', {
      action: 'open',
      prefecture_code: alert.prefecture_code,
    });
    setSelectedAlert(alert);
    setAlertModalShow(true);
  };
  // 描画用にalertByPrefecturesをareaごとに分割.
  const alertByAreaDict = classifyAlertsByArea(alertByPrefectures);

  return (
    <>
      {AlertModal}
      <Box
        sx={{
          background: '#ffffff',
          width: '350px',
          maxWidth: '92vw',
          maxHeight: '430px',
          overflowY: 'auto',
          border: '1px solid #d7dee8',
          borderRadius: '10px',
          boxShadow: '0 10px 26px rgba(15, 23, 42, 0.12)',
        }}
      >
        <Stack direction="row" sx={{ width: '100%', py: 1.2, px: 1.2 }} justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography sx={{ color: '#4a5568', width: '18px', height: '18px' }}>{`▼`}</Typography>
          <Stack direction="row" sx={{ width: '100%' }} justifyContent="space-between" alignItems="flex-start">
            <Typography sx={{ fontSize: '16px', lineHeight: 1.2 }}>
              {t('pages.jmaAlerts.title')}
            </Typography>

            <Typography sx={{ fontSize: '10px', marginRight: '6px', lineHeight: '18px' }}>
              ※ {t('pages.jmaAlerts.currentlyPaused')}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.9} sx={{ px: 1.2, pb: 0.8, flexWrap: 'wrap' }}>
          <LegendBadges />
        </Stack>

        <Stack direction="row" sx={{ px: 1.2, pb: 1.2 }} spacing={1.2} alignItems="center" justifyContent="center">
          <Typography sx={{ fontSize: '10pt', color: '#1a202c', letterSpacing: 0.2 }}>
            {formatLastUpdated(latestUpdated)}
          </Typography>
          <Typography sx={{ fontSize: '10pt', color: '#1a202c' }}>{t('pages.jmaAlerts.publisher')}</Typography>
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            sx={{
              minWidth: 'auto',
              p: 0,
              color: '#1a202c',
              fontSize: '16pt',
              lineHeight: 1,
            }}
          >
            {isRefreshing ? '...' : <AutorenewIcon fontSize="inherit" sx={{ transform: 'rotate(90deg)' }} />}
          </Button>
        </Stack>

        <Stack style={{ marginBottom: '8px' }} spacing={0.6}>
          {areaList.map((area, idx) => {
            const prefectures = alertByAreaDict[area] || [];

            return (
              <React.Fragment key={idx}>
                <AlertByAreaViewer
                  name={area}
                  prefectures={prefectures}
                  handleDetailClick={handleDetailClick}
                />
              </React.Fragment>
            );
          })}
        </Stack>
      </Box>
    </>
  );
};

export default AlertsFromJMA;

const classifyAlertsByArea = (alertByPrefectures: any) => {
  let groupedByArea: any = {};
  Object.values(alertByPrefectures).forEach(alertByP => {
    const alertObj = alertByP as alertByPrefecture;
    if (groupedByArea[alertObj.district]) {
      groupedByArea[alertObj.district].push(alertObj);
    } else {
      groupedByArea[alertObj.district] = [alertObj];
    }
  });
  return groupedByArea;
};

const AlertByAreaViewer = ({
  prefectures,
  name,
  handleDetailClick,
}: {
  prefectures: alertByPrefecture[];
  name: string;
  handleDetailClick: (alert: alertByPrefecture) => () => void;
}) => {
  const { t } = useTranslation();

  const [show, setShow] = useState<boolean>(false);
  return (
    <>
      <Box>
        <Button
          onClick={() => {
            // GA4: 気象警報エリアの開閉を計測
            sendAnalyticsEvent('jma_alert_area', {
              action: !show ? 'expand' : 'collapse',
              area_name: name,
              prefecture_count: prefectures.length,
            });
            setShow(!show);
          }}
          style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: '#000', width: '100%' }}
        >
          <Stack
            direction="row"
            sx={{ width: '100%', px: 1.2, py: 0.45 }}
            justifyContent="flex-start"
            alignItems="center"
            spacing={0.8}
          >
            <span style={{ color: '#4a5568', width: '14px', height: '14px' }}>{show ? `▼` : `▶`}</span>
            <Typography style={{ fontWeight: 700, fontSize: '10.5pt', color: '#2d3748' }}>
              {name}
            </Typography>
            <Typography style={{ fontWeight: 600, fontSize: '9pt', color: '#718096' }}>
              {`(${prefectures.length})`}
            </Typography>
          </Stack>
        </Button>
        {show && (
          <Box sx={{ px: 1.2, pb: 0.6 }}>
            {prefectures.map((p, idx) => {
              const groupedAlertLabels = buildGroupedLabels(p.alerts);

              return (
                <Box
                  key={idx}
                  sx={{
                    borderBottom: idx < prefectures.length - 1 ? '1px solid #e2e8f0' : 'none',
                    py: 0.8,
                  }}
                >
                  <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Typography style={{ fontSize: '10pt', fontWeight: 700, color: '#2d3748' }}>
                      ・{p.prefecture}
                    </Typography>
                    <Button style={{ padding: '0px', minWidth: 'auto' }} onClick={handleDetailClick(p)}>
                      <Typography
                        style={{
                          fontSize: '9pt',
                          textDecoration: 'underline',
                          color: '#2b6cb0',
                          fontWeight: 700,
                        }}
                      >
                        {t('pages.jmaAlerts.detail')}
                      </Typography>
                    </Button>
                  </Box>
                  <Box style={{ marginLeft: '18px', lineHeight: '16px', marginTop: '6px', marginBottom: '2px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {groupedAlertLabels.map((group, groupIdx) => (
                      <AlertCategoryBadge key={`${group.label}-${groupIdx}`} text={group.label} level={group.level} />
                    ))}
                    {groupedAlertLabels.length === 0 && (
                      <Typography style={{ fontSize: '9pt', color: '#718096' }}>{t('pages.jmaAlerts.noInformation')}</Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </>
  );
};

type groupedAlertType = {
  label: string;
  level: Exclude<alertLevelType, 0>;
};

export const LegendBadges: FC<{}> = () => {
  const {t} = useTranslation();
  return (
    <>
      {legendItems.map((item: {
        label: string;
        color: string;
        textColor: string;
      }, i) => {
        const [top, bottom] = t(item.label).split('\n');
        return (
          <Box
            sx={{
              px: 1.1,
              py: 0.55,
              borderRadius: '2px',
              backgroundColor: item.color,
              color: item.textColor,
              width: '72px',
              textAlign: 'center',
              fontWeight: 700,
              lineHeight: 1.12,
            }}
            key={`${i}_${item.textColor}`}
          >
            <Typography sx={{ fontSize: '10px', fontWeight: 700 }}>{top}</Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>{bottom}</Typography>
          </Box>
        );
      })}
    </>
  );
};

const AlertCategoryBadge = ({ text, level }: { text: string; level: Exclude<alertLevelType, 0> }) => {
  const styleByLevel = {
    2: { backgroundColor: '#ffca1a', color: '#1a202c' },
    3: { backgroundColor: '#f40e1a', color: '#ffffff' },
    4: { backgroundColor: '#ad1bb9', color: '#ffffff' },
    5: { backgroundColor: '#2d2a36', color: '#ffffff' },
  } as const;
  return (
    <Box
      sx={{
        px: 1.2,
        height: '22px',
        borderRadius: '2px',
        fontSize: '10pt',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...styleByLevel[level],
      }}
    >
      {text}
    </Box>
  );
};

const buildGroupedLabels = (alerts: alertSummaryType[]): groupedAlertType[] => {
  const groups: Record<string, groupedAlertType> = {};
  alerts.forEach((alert) => {
    if (alert.level === 0 || alert.is_cleared) {
      return;
    }
    const category = alert.category;
    const level = alert.level;
    if (!groups[category] || groups[category].level < level) {
      groups[category] = { label: category, level };
    }
  });
  return Object.values(groups);
};

const formatLastUpdated = (value: string) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const yyyy = date.getFullYear();
  const mm = `${date.getMonth() + 1}`.padStart(2, '0');
  const dd = `${date.getDate()}`.padStart(2, '0');
  const hh = `${date.getHours()}`.padStart(2, '0');
  const min = `${date.getMinutes()}`.padStart(2, '0');
  return `${yyyy}/${mm}/${dd} ${hh}:${min}`;
};
